import React from 'react'
import type { CalendarEvent } from 'paintscout'
import moment from 'moment'
import { makeStyles, Typography } from '@paintscout/ui'
import { useProductionCalendar } from '../ProductionCalendarContext'
import { MonthEvent } from './MonthEvent'

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column'
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 1.5),
    backgroundColor: theme.paintscout.palette.brand.Neutral.P96,
    borderTop: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`,
    borderBottom: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`
  },
  dateLabel: {
    flexShrink: 0,
    color: '#354962',
    fontFamily: 'Inter',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: '20px'
  },
  countBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box',
    minWidth: '28px',
    padding: theme.spacing(0.5, 1),
    borderRadius: theme.borderRadius.lg,
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${theme.paintscout.palette.brand.Neutral.P85}`,
    color: theme.paintscout.palette.brand.Neutral.P60,
    fontSize: theme.typography.pxToRem(12),
    fontWeight: 500,
    lineHeight: 1,
    textAlign: 'center'
  },
  events: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    padding: theme.spacing(1.5, 1)
  },
  noEvents: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(5, 6, 3, 6),
    gap: theme.spacing(2),
    color: theme.paintscout.palette.brand.Neutral.P90
  }
}))

interface MobileEventTableProps {
  events: CalendarEvent[]
}

export default function MobileEventTable({ events }: MobileEventTableProps) {
  const classes = useStyles()
  const { date } = useProductionCalendar()

  const orderedEvents = events.sort((a, b) => {
    const aDuration = (a.end?.getTime() || 0) - (a.start?.getTime() || 0)
    const bDuration = (b.end?.getTime() || 0) - (b.start?.getTime() || 0)
    return aDuration - bDuration
  })

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <Typography variant="subtitle2" className={classes.dateLabel}>
          {moment(date).format('dddd, MMMM D')}
        </Typography>
        {orderedEvents.length > 0 && <span className={classes.countBadge}>{orderedEvents.length}</span>}
      </div>
      {orderedEvents.length > 0 ? (
        <div className={classes.events}>
          {orderedEvents.map((event) => (
            <MonthEvent key={event.id} event={event} />
          ))}
        </div>
      ) : (
        <div className={classes.noEvents}>
          <i className="fa-regular fa-calendar fa-2x" />
          <Typography variant="subtitle1" color="textSecondary">
            No jobs scheduled
          </Typography>
        </div>
      )}
    </div>
  )
}
